import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { AuthCard } from "~/components/auth/auth-card";
import { AuthShell } from "~/components/auth/auth-shell";
import { refreshClerkSessionToken } from "~/lib/auth/refresh-clerk-token";
import { getFinishAccountStateFn } from "~/lib/functions/account-finish";
import { finishAccountDestination } from "~/lib/shared/account-finish";
import { createNoIndexMeta } from "~/lib/shared/meta";

import {
  ExpiredLink,
  FinishAccountForm,
} from "./-components/finish-account-form";
import { ProveEmailStep } from "./-components/prove-email-step";

export const Route = createFileRoute("/finish-account/")({
  staticData: { title: "Finish setting up your account" },
  validateSearch: z.object({
    next: z.string().optional(),
    from: z.literal("checkout").optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ next: finishAccountDestination(search.next) }),
  loader: async ({ deps }) => {
    // A client navigation can carry a token past its 60 seconds, which the
    // server reads as signed out.
    await refreshClerkSessionToken();
    const state = await getFinishAccountStateFn();
    if (state.kind === "finished") throw redirect({ href: deps.next });
    return { state, next: deps.next };
  },
  head: () =>
    createNoIndexMeta(
      "Finish setting up your account – Dream Coach Match",
      "Confirm your email and create your password to finish setting up your account.",
    ),
  component: FinishAccountPage,
});

/**
 * The last steps of creating her account. Back from Checkout she enters a code
 * from her inbox, then sets a password, then goes on. An emailed account link
 * already proved the inbox, so it goes straight to the password.
 */
function FinishAccountPage(): React.JSX.Element {
  const { state, next } = Route.useLoaderData();
  const { from } = Route.useSearch();
  return (
    <AuthShell>
      <AuthCard>
        {state.kind === "ready" && (
          <FinishAccountForm email={state.email} next={next} />
        )}
        {state.kind === "prove_email" && (
          <ProveEmailStep
            email={state.email}
            next={next}
            signedIn={state.signedIn}
            sendOnOpen={from === "checkout"}
          />
        )}
        {state.kind === "expired" && <ExpiredLink next={next} />}
      </AuthCard>
    </AuthShell>
  );
}
